import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, MapPin, MessageCircle, Navigation, Package, Phone, Play, Square, Truck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ChangePassword from "../../components/ChangePassword";

const card = "rounded-2xl border border-adminborder bg-admincard";
const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3.5 py-2.5 text-sm text-slate-200 outline-none focus:border-saffron";

const FLOW = [
  ["reached", "Mark Reached"],
  ["pickup_done", "Pickup Done"],
  ["bill_shared", "Bill Shared"],
  ["amount_taken", "Amount Taken"],
  ["submitted_office", "Submitted in Office"],
];
const TASK_BADGE = {
  scheduled: "bg-sky-500/15 text-sky-400", reached: "bg-violet-500/15 text-violet-400",
  pickup_done: "bg-emerald-500/15 text-emerald-400", bill_shared: "bg-amber-500/15 text-amber-400",
  amount_taken: "bg-teal-500/15 text-teal-400", submitted_office: "bg-slate-500/15 text-slate-400",
};

const waPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  return d.length === 10 ? `91${d}` : d;
};

export default function PickupPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [onDuty, setOnDuty] = useState(!!user?.on_duty);
  const [gps, setGps] = useState("");
  const [gpsDenied, setGpsDenied] = useState(false);
  const [awbInputs, setAwbInputs] = useState({});
  const [myBookings, setMyBookings] = useState([]);
  const watchRef = useRef(null);
  const intervalRef = useRef(null);

  const load = useCallback(() => {
    api.get("/pickup/tasks").then((r) => setTasks(r.data)).catch(() => {});
    api.get("/pickup/my-awbs").then((r) => setMyBookings(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // Auto-prefill AWB when returning from a booking
  useEffect(() => {
    const awb = location.state?.bookedAwb;
    if (!awb || !tasks.length) return;
    const target = tasks.find((t) => t.status !== "submitted_office" && !t.shipment_awb);
    if (target) {
      setAwbInputs((p) => ({ ...p, [target.id]: awb }));
      toast.success(`AWB ${awb} copied — tap Link & Share on ${target.customer_name}'s task`);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [tasks, location.state]);

  const stopGps = useCallback(() => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    watchRef.current = null; intervalRef.current = null;
  }, []);

  const ping = useCallback(() => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => api.post("/pickup/location", { latitude: pos.coords.latitude, longitude: pos.coords.longitude })
        .then(() => setGps(`GPS live · ${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)} · ${new Date().toLocaleTimeString("en-IN")}`))
        .catch(() => {}),
      () => { setGps("GPS permission denied"); setGpsDenied(true); });
  }, []);

  const toggleDuty = async () => {
    try {
      const next = !onDuty;
      await api.post("/pickup/duty", { on: next });
      setOnDuty(next);
      if (next) {
        toast.success("Duty started — GPS sharing ON");
        ping();
        if (!gpsDenied) {
          watchRef.current = navigator.geolocation?.watchPosition(
            (pos) => api.post("/pickup/location", { latitude: pos.coords.latitude, longitude: pos.coords.longitude })
              .then(() => setGps(`GPS live · ${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)} · ${new Date().toLocaleTimeString("en-IN")}`))
              .catch(() => {}),
            () => { setGps("GPS permission denied"); setGpsDenied(true); },
            { enableHighAccuracy: true, maximumAge: 10000 });
          intervalRef.current = setInterval(ping, 30000);
        }
      } else {
        stopGps();
        setGps("");
        toast.success("Duty ended — GPS sharing OFF");
      }
    } catch (err) { toast.error(apiError(err)); }
  };
  useEffect(() => stopGps, [stopGps]);

  const advance = async (task, status) => {
    try {
      await api.post(`/pickup/tasks/${task.id}/status`, { status });
      toast.success(`Status: ${status.replace("_", " ")}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const linkAwb = async (task) => {
    const awb = (awbInputs[task.id] || "").trim();
    if (!awb) { toast.error("Enter the AWB from your booking"); return; }
    try {
      const r = await api.post(`/pickup/tasks/${task.id}/awb`, { awb });
      const origin = window.location.origin;
      const bill = r.data.bill_amount;
      const text = `Hello ${task.customer_name}! Your INDIAFAST shipment is booked.${bill ? ` Your bill: Rs.${Number(bill).toLocaleString("en-IN")} (incl. GST) — download the bill PDF: ${origin}/api/public/bill/${r.data.awb}.pdf?phone=${String(task.phone || "").replace(/\D/g, "").slice(-4)}` : ""} Track live: ${origin}/track/${r.data.awb}`;
      toast.success("AWB linked — opening WhatsApp to share bill PDF + tracking");
      window.open(`https://wa.me/${waPhone(task.phone)}?text=${encodeURIComponent(text)}`, "_blank");
      setAwbInputs({ ...awbInputs, [task.id]: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const shareBill = (task) => {
    const amt = task.bill_amount ?? task.quoted_price;
    const origin = window.location.origin;
    const text = `Hello ${task.customer_name}! Your INDIAFAST shipment bill: Rs.${Number(amt || 0).toLocaleString("en-IN")} (incl. GST).${task.shipment_awb ? ` Download your bill PDF: ${origin}/api/public/bill/${task.shipment_awb}.pdf?phone=${String(task.phone || "").replace(/\D/g, "").slice(-4)} · Track: ${origin}/track/${task.shipment_awb}` : ""} Please keep the amount ready. Thank you!`;
    window.open(`https://wa.me/${waPhone(task.phone)}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const active = tasks.filter((t) => t.status !== "submitted_office");
  const done = tasks.filter((t) => t.status === "submitted_office");

  return (
    <div className="min-h-screen bg-adminbg text-slate-200" data-testid="pickup-panel">
      <header className="sticky top-0 z-40 border-b border-adminborder bg-admincard/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 px-4 py-3.5">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-saffron text-white"><Truck className="h-5 w-5" /></span>
            <div>
              <p className="font-heading text-sm font-extrabold text-white">INDIAFAST <span className="text-saffron">Pickup Partner</span></p>
              <p className="text-[11px] text-slate-500">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleDuty} data-testid="duty-toggle-btn"
                    className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white transition-colors ${onDuty ? "bg-red-500 hover:bg-red-600" : "bg-emerald-500 hover:bg-emerald-600"}`}>
              {onDuty ? <><Square className="h-3.5 w-3.5" /> End Duty</> : <><Play className="h-3.5 w-3.5" /> Start Duty</>}
            </button>
            <ChangePassword testid="pickup-change-password-btn" iconOnly
                            triggerClass="rounded-lg border border-adminborder p-2 text-slate-400 hover:text-white" />
            <button onClick={async () => { stopGps(); await logout(); navigate("/"); }} data-testid="pickup-logout-btn"
                    className="rounded-lg border border-adminborder p-2 text-slate-400 hover:text-white"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
        {onDuty && <p className="border-t border-adminborder bg-emerald-500/5 px-4 py-1.5 text-center text-[11px] font-semibold text-emerald-400" data-testid="gps-status">{gps || "Acquiring GPS…"}</p>}
      </header>

      <main className="mx-auto max-w-3xl space-y-4 px-4 py-6">
        {!active.length && (
          <div className={`${card} py-14 text-center`} data-testid="pickup-empty">
            <Package className="mx-auto h-10 w-10 text-slate-600" />
            <p className="mt-3 text-sm text-slate-500">No active pickups. New assignments from CRM appear here automatically.</p>
          </div>
        )}
        {active.map((t) => {
          const next = FLOW[FLOW.findIndex(([s]) => s === t.status) + 1];
          return (
            <div key={t.id} className={`${card} p-5`} data-testid={`pickup-task-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-bold text-white">{t.customer_name}</p>
                  <p className="mt-0.5 text-xs text-slate-400">{t.address}</p>
                  <p className="mt-1 text-xs text-slate-500">{t.country} · ~{t.approx_kg} kg{t.special_items ? ` · ${t.special_items}` : ""}{t.quoted_price ? ` · Quoted ₹${Number(t.quoted_price).toLocaleString("en-IN")}` : ""}</p>
                </div>
                <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold uppercase ${TASK_BADGE[t.status]}`}>{t.status?.replace("_", " ")}</span>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2">
                <a href={`tel:${t.phone}`} data-testid={`task-call-${t.id}`}
                   className="flex items-center justify-center gap-1.5 rounded-lg border border-adminborder py-2.5 text-xs font-bold text-slate-200 hover:border-saffron">
                  <Phone className="h-4 w-4" /> Call
                </a>
                <a href={`https://wa.me/${waPhone(t.phone)}`} target="_blank" rel="noreferrer" data-testid={`task-whatsapp-${t.id}`}
                   className="flex items-center justify-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 py-2.5 text-xs font-bold text-emerald-400 hover:bg-emerald-500/20">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
                <a href={t.lat && t.lng ? `https://www.google.com/maps/dir/?api=1&destination=${t.lat},${t.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(t.address || "")}`} target="_blank" rel="noreferrer" data-testid={`task-navigate-${t.id}`}
                   className="flex items-center justify-center gap-1.5 rounded-lg border border-sky-500/40 bg-sky-500/10 py-2.5 text-xs font-bold text-sky-400 hover:bg-sky-500/20">
                  <Navigation className="h-4 w-4" /> Navigate
                </a>
              </div>

              {next && (
                <button onClick={() => advance(t, next[0])} data-testid={`task-advance-${t.id}`}
                        className="mt-3 w-full rounded-lg bg-saffron py-2.5 text-sm font-bold text-white hover:bg-saffron-hover">
                  {next[1]}
                </button>
              )}

              {(t.status === "pickup_done" || t.status === "reached") && (
                <button onClick={() => navigate("/app/book")} data-testid={`task-book-${t.id}`}
                        className="mt-2 w-full rounded-lg border border-saffron/50 bg-saffron/10 py-2.5 text-sm font-bold text-saffron hover:bg-saffron/20">
                  Book Shipment (like agent)
                </button>
              )}

              {t.status !== "scheduled" && (
                <div className="mt-3 space-y-2">
                  {!t.shipment_awb ? (
                    <div className="flex gap-2">
                      <input className={darkInput} data-testid={`task-awb-${t.id}`} placeholder="Enter or pick AWB from your bookings"
                             list={`awb-list-${t.id}`} autoComplete="off"
                             value={awbInputs[t.id] || ""} onChange={(e) => setAwbInputs({ ...awbInputs, [t.id]: e.target.value })} />
                      <datalist id={`awb-list-${t.id}`}>
                        {myBookings.map((b) => (
                          <option key={b.awb} value={b.awb}>{`${b.awb} · ${b.destination} · ₹${Number(b.amount || 0).toLocaleString("en-IN")}`}</option>
                        ))}
                      </datalist>
                      <button onClick={() => linkAwb(t)} data-testid={`task-link-awb-${t.id}`}
                              className="shrink-0 rounded-lg bg-emerald-500 px-3.5 text-xs font-bold text-white hover:bg-emerald-600">Link & Share</button>
                    </div>
                  ) : (
                    <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-400">AWB {t.shipment_awb} linked</p>
                  )}
                  <button onClick={() => shareBill(t)} data-testid={`task-share-bill-${t.id}`}
                          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-emerald-500/40 py-2 text-xs font-bold text-emerald-400 hover:bg-emerald-500/10">
                    <MessageCircle className="h-3.5 w-3.5" /> Share Bill PDF on WhatsApp
                  </button>
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-1.5">
                {(t.status_log || []).map((s, i) => (
                  <span key={i} className="rounded-full border border-adminborder px-2 py-0.5 text-[10px] text-slate-500">
                    {s.status?.replace("_", " ")} · {new Date(s.at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                ))}
              </div>
            </div>
          );
        })}

        {done.length > 0 && (
          <div className={`${card} p-4`} data-testid="pickup-history">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Completed</p>
            {done.slice(0, 10).map((t) => (
              <div key={t.id} className="flex items-center justify-between border-b border-adminborder/60 py-2 text-xs last:border-0">
                <span className="text-slate-300">{t.customer_name}{t.shipment_awb ? ` · ${t.shipment_awb}` : ""}</span>
                <span className="text-slate-500">{new Date(t.created_at).toLocaleDateString("en-IN")}</span>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
